import type { OilType, RawMaterial } from "~/types/purchasing";
import type { Product } from "~/types/product";
import type {
  AdjustmentReasonCode,
  ItemType,
  Plant,
  StockAdjustmentFormData,
} from "~/types/stockAdjustment";
import type { Warehouse } from "~/types/wms";

import type { StockByPlantMap } from "~/hooks/purchasing";

export type Direction = "increment" | "decrement";

// Item picker value format: "<itemType>:<id>" — ids are globally unique, but the
// prefix carries the type so we never have to guess which catalog it came from.
export function itemKeyOf(itemType: ItemType, id: string): string {
  return `${itemType}:${id}`;
}

export function parseItemKey(
  key: string
): { itemType: ItemType; id: string } | null {
  const i = key.indexOf(":");
  if (i < 0) return null;
  const itemType = key.slice(0, i) as ItemType;
  const id = key.slice(i + 1);
  if (!id) return null;
  if (itemType !== "product" && itemType !== "oil" && itemType !== "raw_material")
    return null;
  return { itemType, id };
}

export function normalizeTipo(raw: string): ItemType | null {
  const s = raw.toLowerCase();
  if (/producto|product|terminad|\bpt\b/.test(s)) return "product";
  if (/aceite|oil/.test(s)) return "oil";
  if (/materia|raw|insumo|\bmp\b/.test(s)) return "raw_material";
  return null;
}

// Entered oil quantity → liters (its stock unit) for the moving-average weight.
export function oilQtyToLiters(qty: number, unit: string, density: number): number {
  if (unit === "tonnes") return (qty * 1000) / density;
  if (unit === "kg") return qty / density;
  return qty; // liters / L
}

// Resolve a code (from a file import) to a single catalog item within its Tipo.
// Codes can collide across tables, so Tipo disambiguates; a code that matches 0 or
// >1 rows in its own table is reported as an error rather than guessed.
export function resolveCode(
  tipoRaw: string,
  codigo: string,
  products: Product[],
  oilTypes: OilType[],
  rawMaterials: RawMaterial[]
):
  | { itemType: ItemType; id: string; label: string }
  | { error: string } {
  const tipo = normalizeTipo(tipoRaw);
  if (!tipo) return { error: `Tipo no reconocido: "${tipoRaw || "(vacío)"}"` };
  if (tipo === "product") {
    const m = products.filter((p) => p.sku === codigo);
    if (m.length === 0) return { error: `Producto no encontrado: ${codigo}` };
    if (m.length > 1) return { error: `Código ambiguo: ${codigo}` };
    return { itemType: "product", id: m[0].id, label: m[0].short_name ?? "" };
  }
  if (tipo === "oil") {
    const m = oilTypes.filter((o) => o.code === codigo);
    if (m.length === 0) return { error: `Aceite no encontrado: ${codigo}` };
    if (m.length > 1) return { error: `Código ambiguo: ${codigo}` };
    return { itemType: "oil", id: m[0].id, label: m[0].name };
  }
  const m = rawMaterials.filter((r) => r.code === codigo);
  if (m.length === 0) return { error: `Materia prima no encontrada: ${codigo}` };
  if (m.length > 1) return { error: `Código ambiguo: ${codigo}` };
  return { itemType: "raw_material", id: m[0].id, label: m[0].name };
}

export interface EvaluateParams {
  itemType: ItemType | null;
  product?: Product | null;
  oil?: OilType | null;
  rawMaterial?: RawMaterial | null;
  location: string; // Bodega code (product) or Plant (ingredient)
  signedQty: number | null; // + increment / − decrement; null/0 = incomplete
  unitCost: number | null;
  lot: string | null;
  notes: string | null;
  reason: AdjustmentReasonCode | null;
  warehouses: Warehouse[];
  oilStock: StockByPlantMap | undefined;
  rmStock: StockByPlantMap | undefined;
}

export interface BulkRowEval {
  // A row is `complete` once an item and a non-zero quantity are set; only then do
  // we surface validation errors (so half-filled rows don't flash red).
  complete: boolean;
  ok: boolean;
  error?: string;
  hasPreview: boolean;
  direction: Direction;
  absQty: number;
  avgBefore: number;
  avgAfter: number;
  costChanged: boolean;
  data?: StockAdjustmentFormData;
}

const incomplete: BulkRowEval = {
  complete: false,
  ok: false,
  hasPreview: false,
  direction: "increment",
  absQty: 0,
  avgBefore: 0,
  avgAfter: 0,
  costChanged: false,
};

// Validate + preview a single row and, when valid, produce the StockAdjustmentFormData
// the per-row RPC path consumes. Mirrors StockAdjustmentDialog's per-item math so the
// preview matches what will actually happen.
export function evaluateBulkRow(p: EvaluateParams): BulkRowEval {
  if (!p.itemType) return incomplete;
  if (p.signedQty == null || p.signedQty === 0) return incomplete;

  const direction: Direction = p.signedQty > 0 ? "increment" : "decrement";
  const absQty = Math.abs(p.signedQty);
  const base = { ...incomplete, complete: true, direction, absQty };
  const fail = (error: string): BulkRowEval => ({ ...base, error });

  if (!p.reason) return fail("Selecciona un motivo");
  if (
    p.reason.adjustment_type !== "both" &&
    p.reason.adjustment_type !== direction
  ) {
    return fail(
      `El motivo solo permite ${
        p.reason.adjustment_type === "increment"
          ? "incrementos (+)"
          : "decrementos (−)"
      }`
    );
  }

  const notes = p.notes ?? undefined;

  // ---- Producto terminado ----
  if (p.itemType === "product") {
    const product = p.product;
    if (!product) return fail("Producto no válido");
    if (!Number.isInteger(absQty))
      return fail("La cantidad de producto debe ser entera");
    const wh = p.warehouses.find(
      (w) => w.is_active && w.code.toUpperCase() === p.location.toUpperCase()
    );
    if (!p.location) return fail("Falta la bodega");
    if (!wh) return fail(`Bodega inválida: "${p.location}"`);
    if (direction === "increment") {
      if (p.reason.requires_lot_number && !p.lot)
        return fail("El motivo requiere lote");
      if (p.reason.requires_expiration_date)
        return fail("El motivo requiere vencimiento (usa el ajuste individual)");
    } else if (!p.lot) {
      return fail("Falta lote para el decremento");
    }

    const avgBefore = product.unit_cost_clp ?? 0;
    const stock = product.stock ?? 0;
    const cost = direction === "increment" ? p.unitCost : null;
    const avgAfter =
      cost == null
        ? avgBefore
        : stock <= 0
          ? cost
          : (stock * avgBefore + absQty * cost) / (stock + absQty);

    const data: StockAdjustmentFormData = {
      adjustment_type: direction,
      reason_code_id: p.reason.id,
      item_type: "product",
      product_id: product.id,
      warehouse_code: wh.code,
      lot_number: p.lot ?? undefined,
      unit_cost: direction === "increment" ? (p.unitCost ?? undefined) : undefined,
      production_batch_id: null,
      quantity: absQty,
      notes,
    };
    return {
      ...base,
      ok: true,
      hasPreview: avgBefore > 0 || avgAfter > 0,
      avgBefore,
      avgAfter,
      costChanged: Math.abs(avgAfter - avgBefore) > 0.005,
      data,
    };
  }

  // ---- Ingredientes: plant location ----
  const plant = p.location.toUpperCase() as Plant;
  if (!p.location) return fail("Falta la planta");
  if (plant !== "P1" && plant !== "P2")
    return fail(`Planta inválida: "${p.location}"`);

  if (p.itemType === "oil") {
    const oil = p.oil;
    if (!oil) return fail("Aceite no válido");
    const density = oil.density_kg_per_liter || 0.92;
    const unit = "kg"; // oils entered in kg by default (recipes are in kg)
    const avgBefore = oil.average_cost_clp ?? 0; // per liter
    const plantStock = p.oilStock?.get(oil.id)?.[plant] ?? 0;
    const cost = direction === "increment" ? p.unitCost : null;
    const costPerL = cost == null ? null : cost * density; // $/kg → $/L
    const qtyL = oilQtyToLiters(absQty, unit, density);
    const avgAfter =
      costPerL == null
        ? avgBefore
        : plantStock <= 0
          ? costPerL
          : (plantStock * avgBefore + qtyL * costPerL) / (plantStock + qtyL);

    const data: StockAdjustmentFormData = {
      adjustment_type: direction,
      reason_code_id: p.reason.id,
      item_type: "oil",
      oil_type_id: oil.id,
      plant,
      unit,
      unit_cost: direction === "increment" ? (p.unitCost ?? undefined) : undefined,
      quantity: absQty,
      notes,
    };
    return {
      ...base,
      ok: true,
      hasPreview: avgBefore > 0 || avgAfter > 0,
      avgBefore,
      avgAfter,
      costChanged: Math.abs(avgAfter - avgBefore) > 0.005,
      data,
    };
  }

  // ---- Materia prima ----
  const material = p.rawMaterial;
  if (!material) return fail("Materia prima no válida");
  const avgBefore = material.average_cost_clp ?? 0;
  const plantStock = p.rmStock?.get(material.id)?.[plant] ?? 0;
  const cost = direction === "increment" ? p.unitCost : null;
  const avgAfter =
    cost == null
      ? avgBefore
      : plantStock <= 0
        ? cost
        : (plantStock * avgBefore + absQty * cost) / (plantStock + absQty);

  const data: StockAdjustmentFormData = {
    adjustment_type: direction,
    reason_code_id: p.reason.id,
    item_type: "raw_material",
    raw_material_id: material.id,
    plant,
    unit: material.unit,
    unit_cost: direction === "increment" ? (p.unitCost ?? undefined) : undefined,
    quantity: absQty,
    notes,
  };
  return {
    ...base,
    ok: true,
    hasPreview: avgBefore > 0 || avgAfter > 0,
    avgBefore,
    avgAfter,
    costChanged: Math.abs(avgAfter - avgBefore) > 0.005,
    data,
  };
}
